import "./styles.css";
import "bootstrap/dist/css/bootstrap.min.css";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import { BrowserRouter, Route, Routes, useNavigate, useParams } from "react-router-dom";
import {
  MdClose,
  MdDeleteOutline,
  MdLocationOn,
  MdMessage,
  MdRemoveShoppingCart,
  MdSearch,
  MdShoppingCart,
  MdStorefront,
  MdWhatsapp,
} from "react-icons/md";
import CartPage from "./pages/Cart/CartPage";
import CheckoutPage from "./pages/Checkout/CheckoutPage";
import ReceiptPage from "./pages/Checkout/ReceiptPage";
import FlashSalePage from "./pages/FlashSale/FlashSalePage";
import HomePage from "./pages/Home/HomePage";
import DetailPage from "./pages/Product/DetailPage";
import { formatRupiah, getProductImage, getVisitorId } from "./utils/ecommerce";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5007";

const StorefrontRoutes = () => {
  const navigate = useNavigate();
  const flashRef = useRef(null);
  const rekomendasiRef = useRef(null);

  const [barangs, setBarangs] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [selectedKategori, setSelectedKategori] = useState("SEMUA");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [visitorId] = useState(getVisitorId);
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  const [checkoutForm, setCheckoutForm] = useState({
    nama: "",
    alamatPengiriman: "",
    nomorHandphone: "",
  });
  const [orderResult, setOrderResult] = useState(null);
  const [adminWhatsapp, setAdminWhatsapp] = useState(null);
  const [informasiToko, setInformasiToko] = useState(null);
  const [showCartDrawer, setShowCartDrawer] = useState(false);
  const [showTransferPopup, setShowTransferPopup] = useState(
    () => sessionStorage.getItem("ajs_transfer_popup_seen") !== "1",
  );
  const [toast, setToast] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const applyCartPayload = (payload) => {
    setCartItems(payload.items || []);
    setCartCount(Number(payload.totalQty || 0));
    setCartTotal(Number(payload.totalHarga || 0));
  };

  const getCart = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/public/keranjang`, {
        params: {
          visitorId,
        },
      });
      applyCartPayload(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Belum bisa mengambil data keranjang.");
    }
  }, [visitorId]);

  useEffect(() => {
    getCart();
  }, [getCart]);

  useEffect(() => {
    const getAdminWhatsapp = async () => {
      try {
        const response = await axios.get(`${API_URL}/public/adminWhatsapp`);
        setAdminWhatsapp(response.data);
      } catch (err) {
        setAdminWhatsapp(null);
      }
    };

    getAdminWhatsapp();
  }, []);

  useEffect(() => {
    const getInformasiToko = async () => {
      try {
        const response = await axios.get(`${API_URL}/public/informasiToko`);
        setInformasiToko(response.data);
      } catch (err) {
        setInformasiToko(null);
      }
    };

    getInformasiToko();
  }, []);

  useEffect(() => {
    const getBarangs = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await axios.get(`${API_URL}/public/barangs`, {
          params: {
            search_query: submittedSearch,
            page: 0,
            limit: 80,
          },
        });
        setBarangs(response.data.barangs || []);
      } catch (err) {
        setError(
          err.response?.data?.message || "Belum bisa mengambil katalog produk dari server.",
        );
      }

      setLoading(false);
    };

    getBarangs();
  }, [submittedSearch]);

  const kategoris = useMemo(() => {
    const kategoriMap = new Map();

    barangs.forEach((barang) => {
      if (barang.kategori?.id && barang.kategori?.nama) {
        kategoriMap.set(barang.kategori.id, {
          id: barang.kategori.id,
          nama: barang.kategori.nama,
          image: getProductImage(barang),
        });
      }
    });

    return Array.from(kategoriMap.values());
  }, [barangs]);

  const filteredBarangs = useMemo(() => {
    if (selectedKategori === "SEMUA") {
      return barangs;
    }

    return barangs.filter(
      (barang) => String(barang.kategori?.id) === String(selectedKategori),
    );
  }, [barangs, selectedKategori]);

  const flashSaleProducts = barangs.filter((barang) => barang.isFlashsale);
  const whatsappNumber =
    informasiToko?.nomorWhatsapp || adminWhatsapp?.nomorWhatsapp || "08123473727";
  const storeName = informasiToko?.namaToko || "Ajs Elektronik";
  const storeTagline = informasiToko?.tagline || "ALL HOME APPLIANCES YOU NEED";
  const storeAddress =
    informasiToko?.alamat ||
    "Komplek Arteri Mas, 65 - 65B, Jl. Panjang Arteri Klp. Dua Raya";
  const storeEmail = informasiToko?.email || "info@ajs.example.com";
  const warningTransfer =
    informasiToko?.warningTransfer ||
    "Pembayaran hanya sah jika ditransfer ke rekening resmi atas nama CV Ajs Elektronik.";

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(""), 2200);
  };

  const closeTransferPopup = () => {
    sessionStorage.setItem("ajs_transfer_popup_seen", "1");
    setShowTransferPopup(false);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    setSelectedKategori("SEMUA");
    setSubmittedSearch(searchText.trim());
    navigate("/");
  };

  const scrollRow = (ref, direction) => {
    if (!ref.current) {
      return;
    }

    ref.current.scrollBy({
      left: direction * 720,
      behavior: "smooth",
    });
  };

  const openProduct = (barang) => {
    setSelectedImageIndex(0);
    setQuantity(1);
    navigate(`/produk/${barang.id}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToHome = () => {
    setSearchText("");
    setSubmittedSearch("");
    setSelectedKategori("SEMUA");
    navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const addToCart = async (barang, qty = 1) => {
    try {
      const response = await axios.post(`${API_URL}/public/keranjang/add`, {
        visitorId,
        barangId: barang.id,
        qty,
      });
      applyCartPayload(response.data);
      showToast(`${barang.nama} masuk keranjang.`);
      setShowCartDrawer(true);
    } catch (err) {
      showToast(err.response?.data?.message || "Gagal menambahkan keranjang.");
    }
  };

  const updateCartItem = async (item, qty) => {
    try {
      const response = await axios.post(`${API_URL}/public/keranjang/update`, {
        visitorId,
        itemId: item.id,
        qty,
      });
      applyCartPayload(response.data);
    } catch (err) {
      showToast(err.response?.data?.message || "Gagal mengubah keranjang.");
    }
  };

  const deleteCartItem = async (item) => {
    try {
      const response = await axios.post(`${API_URL}/public/keranjang/delete`, {
        visitorId,
        itemId: item.id,
      });
      applyCartPayload(response.data);
    } catch (err) {
      showToast(err.response?.data?.message || "Gagal menghapus keranjang.");
    }
  };

  const buyNow = async (barang) => {
    await addToCart(barang, quantity);
    navigate("/checkout");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitCheckout = async (event) => {
    event.preventDefault();

    if (!checkoutForm.nama || !checkoutForm.alamatPengiriman || !checkoutForm.nomorHandphone) {
      showToast("Nama, alamat, dan nomor handphone wajib diisi.");
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/public/checkout`, {
        visitorId,
        ...checkoutForm,
      });
      setOrderResult(response.data.pesanan);
      setAdminWhatsapp(response.data.adminWhatsapp || adminWhatsapp);
      applyCartPayload({ items: [], totalQty: 0, totalHarga: 0 });
      navigate("/checkout/berhasil");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      showToast(err.response?.data?.message || "Gagal menyimpan checkout.");
    }
  };

  const getWhatsappUrl = () => {
    if (!orderResult || !whatsappNumber) {
      return "#";
    }

    const message = `Halo Admin, saya ingin konfirmasi order ${orderResult.nomorOrder} atas nama ${orderResult.nama} dengan total ${formatRupiah(orderResult.totalHarga)}.`;
    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
  };

  const getAskProductWhatsappUrl = (barang) => {
    const message = `Halo, saya ingin tanya tentang produk: ${barang.nama}`;
    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
  };

  const getCartWhatsappUrl = () => {
    let message = `Halo ${storeName}, saya ingin memesan:\n\n`;
    let total = 0;

    cartItems.forEach((item, index) => {
      const subtotal = Number(item.qty || 0) * Number(item.harga || 0);
      total += subtotal;
      message += `${index + 1}. ${item.barang?.nama || "-"}\n`;
      message += `   ${item.qty} x ${formatRupiah(item.harga)} = ${formatRupiah(subtotal)}\n`;
    });

    message += `\nTotal: ${formatRupiah(total)}\n\n`;
    message += "Mohon informasi ketersediaan barang dan cara pembayarannya. Terima kasih.";
    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
  };

  const clearCart = async () => {
    try {
      const response = await axios.post(`${API_URL}/public/keranjang/clear`, {
        visitorId,
      });
      applyCartPayload(response.data);
      showToast("Keranjang dikosongkan.");
    } catch (err) {
      showToast(err.response?.data?.message || "Gagal mengosongkan keranjang.");
    }
  };

  const downloadOrderPdf = () => {
    if (!orderResult) {
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    let y = 18;

    doc.setFillColor(238, 77, 45);
    doc.rect(0, 0, pageWidth, 32, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.text("AJS ECOMMERCE", margin, 14);
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text("Bukti Checkout / Invoice Pesanan", margin, 23);

    y = 44;
    doc.setTextColor(34, 34, 34);
    doc.setDrawColor(230, 230, 230);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 42, 3, 3);
    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text("Data Pesanan", margin + 6, y + 10);
    doc.setFont(undefined, "normal");
    doc.text("Nomor Order", margin + 6, y + 20);
    doc.text(`: ${orderResult.nomorOrder}`, margin + 44, y + 20);
    doc.text("Tanggal", margin + 6, y + 29);
    doc.text(`: ${new Date(orderResult.createdAt || Date.now()).toLocaleString("id-ID")}`, margin + 44, y + 29);
    doc.text("Status", margin + 6, y + 38);
    doc.text(`: ${orderResult.status}`, margin + 44, y + 38);

    y += 52;
    doc.roundedRect(margin, y, pageWidth - margin * 2, 43, 3, 3);
    doc.setFont(undefined, "bold");
    doc.text("Data Pengiriman", margin + 6, y + 10);
    doc.setFont(undefined, "normal");
    doc.text("Nama", margin + 6, y + 20);
    doc.text(`: ${orderResult.nama}`, margin + 44, y + 20);
    doc.text("No HP", margin + 6, y + 29);
    doc.text(`: ${orderResult.nomorHandphone}`, margin + 44, y + 29);
    doc.text("Alamat", margin + 6, y + 38);
    doc.text(`: ${orderResult.alamatPengiriman}`, margin + 44, y + 38, { maxWidth: 136 });

    y += 55;
    doc.setFillColor(255, 245, 241);
    doc.rect(margin, y, pageWidth - margin * 2, 10, "F");
    doc.setFont(undefined, "bold");
    doc.text("Produk", margin + 4, y + 7);
    doc.text("Qty", pageWidth - 64, y + 7);
    doc.text("Subtotal", pageWidth - 38, y + 7);

    y += 16;
    doc.setFont(undefined, "normal");
    (orderResult.items || []).forEach((item, index) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }

      doc.text(`${index + 1}. ${item.namaBarang}`, margin + 4, y, { maxWidth: 106 });
      doc.text(String(item.qty), pageWidth - 62, y);
      doc.text(formatRupiah(item.subtotal), pageWidth - 38, y, { align: "left" });
      y += 11;
    });

    doc.setDrawColor(238, 77, 45);
    doc.line(margin, y, pageWidth - margin, y);
    y += 10;
    doc.setFontSize(13);
    doc.setFont(undefined, "bold");
    doc.setTextColor(238, 77, 45);
    doc.text("Total", pageWidth - 76, y);
    doc.text(formatRupiah(orderResult.totalHarga), pageWidth - 14, y, { align: "right" });

    y += 18;
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, "normal");
    doc.text("Terima kasih sudah berbelanja di AJS Ecommerce.", margin, y);
    doc.save(`${orderResult.nomorOrder}.pdf`);
  };

  const DetailRoute = () => {
    const { id } = useParams();
    const selectedBarang = barangs.find((barang) => String(barang.id) === String(id));

    return (
      <DetailPage
        selectedBarang={selectedBarang}
        selectedImageIndex={selectedImageIndex}
        setSelectedImageIndex={setSelectedImageIndex}
        quantity={quantity}
        setQuantity={setQuantity}
        backToHome={backToHome}
        addToCart={addToCart}
        buyNow={buyNow}
        getAskProductWhatsappUrl={getAskProductWhatsappUrl}
        relatedProducts={barangs
          .filter(
            (barang) =>
              String(barang.id) !== String(selectedBarang?.id) &&
              String(barang.kategori?.id) === String(selectedBarang?.kategori?.id),
          )
          .slice(0, 4)}
        openProduct={openProduct}
      />
    );
  };

  const AboutPage = () => (
    <main className="page-container">
      <section className="content-card">
        <div className="section-title">
          <h2>Tentang Kami</h2>
          <div className="line" />
        </div>
        <p>
          {storeName} menyediakan berbagai kebutuhan elektronik rumah tangga dan dapur
          dengan pilihan produk berkualitas, harga kompetitif, dan layanan konsultasi
          melalui WhatsApp.
        </p>
        <div className="feature-grid">
          <div>
            <b>Produk Lengkap</b>
            <span>AC, TV, kulkas, mesin cuci, dispenser, kipas angin, dan produk rumah tangga lain.</span>
          </div>
          <div>
            <b>Garansi Resmi</b>
            <span>Produk dummy disiapkan dengan narasi garansi resmi untuk kebutuhan preview client.</span>
          </div>
          <div>
            <b>Konsultasi Cepat</b>
            <span>Customer bisa langsung bertanya melalui WhatsApp toko.</span>
          </div>
        </div>
      </section>
    </main>
  );

  const ContactPage = () => {
    const [contactForm, setContactForm] = useState({
      nama: "",
      nomorHandphone: "",
      pesan: "",
    });

    const submitContact = (event) => {
      event.preventDefault();
      if (!contactForm.nama || !contactForm.pesan) {
        showToast("Nama dan pesan wajib diisi.");
        return;
      }

      const message = `Halo, saya ${contactForm.nama}${
        contactForm.nomorHandphone ? ` (${contactForm.nomorHandphone})` : ""
      }.\n\n${contactForm.pesan}`;
      window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, "_blank");
    };

    return (
      <main className="page-container">
        <section className="contact-grid">
          <div className="content-card">
            <div className="section-title">
              <h2>Kontak</h2>
              <div className="line" />
            </div>
            <p>{storeAddress}</p>
            <p>WhatsApp: +{whatsappNumber}</p>
            <p>Email: {storeEmail}</p>
          </div>
          <form className="content-card contact-form" onSubmit={submitContact}>
            <label>
              Nama
              <input
                value={contactForm.nama}
                onChange={(event) => setContactForm({ ...contactForm, nama: event.target.value })}
              />
            </label>
            <label>
              Nomor Handphone
              <input
                value={contactForm.nomorHandphone}
                onChange={(event) =>
                  setContactForm({ ...contactForm, nomorHandphone: event.target.value })
                }
              />
            </label>
            <label>
              Pesan
              <textarea
                rows={5}
                value={contactForm.pesan}
                onChange={(event) => setContactForm({ ...contactForm, pesan: event.target.value })}
              />
            </label>
            <button type="submit">Kirim via WhatsApp</button>
          </form>
        </section>
      </main>
    );
  };

  const CartDrawer = () => (
    <>
      <div
        className={showCartDrawer ? "cart-overlay open" : "cart-overlay"}
        onClick={() => setShowCartDrawer(false)}
      />
      <aside className={showCartDrawer ? "cart-drawer open" : "cart-drawer"}>
        <div className="drawer-header">
          <h3>
            <MdShoppingCart /> Keranjang Belanja
          </h3>
          <button type="button" onClick={() => setShowCartDrawer(false)}>
            <MdClose />
          </button>
        </div>
        <div className="drawer-items">
          {cartItems.length === 0 ? (
            <div className="drawer-empty">Keranjang belanja masih kosong</div>
          ) : (
            cartItems.map((item) => (
              <div className="drawer-item" key={item.id}>
                <img src={getProductImage(item.barang)} alt={item.barang?.nama} />
                <div>
                  <b>{item.barang?.nama}</b>
                  <span>{formatRupiah(item.harga)}</span>
                  {item.barang?.minimalQtyGrosir > 0 && (
                    <small>
                      Grosir mulai {item.barang.minimalQtyGrosir} pcs:{" "}
                      {formatRupiah(item.barang.hargaGrosir)}
                    </small>
                  )}
                  <div className="drawer-qty">
                    <button type="button" onClick={() => updateCartItem(item, Number(item.qty) - 1)}>
                      -
                    </button>
                    <span>{item.qty}</span>
                    <button type="button" onClick={() => updateCartItem(item, Number(item.qty) + 1)}>
                      +
                    </button>
                  </div>
                  <button className="drawer-remove" type="button" onClick={() => deleteCartItem(item)}>
                    <MdDeleteOutline /> Hapus
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="drawer-footer">
          <div>
            <span>Total</span>
            <b>{formatRupiah(cartTotal)}</b>
          </div>
          <a
            className={cartItems.length === 0 ? "disabled" : ""}
            href={cartItems.length === 0 ? undefined : getCartWhatsappUrl()}
            target="_blank"
            rel="noreferrer"
          >
            <MdMessage /> Checkout via WhatsApp
          </a>
          <button type="button" disabled={cartItems.length === 0} onClick={() => navigate("/checkout")}>
            <MdShoppingCart />
            Checkout Form
          </button>
          <button type="button" disabled={cartItems.length === 0} onClick={clearCart}>
            <MdRemoveShoppingCart />
            Kosongkan Keranjang
          </button>
        </div>
      </aside>
    </>
  );

  return (
    <div className="storefront">
      {showTransferPopup && (
        <div className="transfer-popup-overlay">
          <div className="transfer-popup">
            <div className="transfer-icon">!</div>
            <h3>Perhatian Sebelum Transfer</h3>
            <p>{warningTransfer}</p>
            <p>
              Ragu atau ingin konfirmasi dulu? Hubungi WhatsApp resmi kami di{" "}
              <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noreferrer">
                +{whatsappNumber}
              </a>
              .
            </p>
            <button type="button" onClick={closeTransferPopup}>
              Saya Mengerti
            </button>
          </div>
        </div>
      )}

      <div className="header-top">
        <div className="page-container topbar-inner">
          <span className="topbar-address" title={storeAddress}>
            <MdLocationOn /> {storeAddress}
          </span>
          <div className="topbar-actions">
            <button type="button" onClick={() => navigate("/tentang-kami")}>
              Tentang Kami
            </button>
            <button type="button" onClick={() => navigate("/kontak")}>
              Kontak
            </button>
            <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noreferrer">
              +{whatsappNumber}
            </a>
          </div>
        </div>
      </div>
      <header className="site-header">
        <div className="page-container header-main">
        <button className="brand" type="button" onClick={backToHome}>
          <div className="brand-icon">
            <MdStorefront />
          </div>
          <div>
            <div className="brand-name">{storeName}</div>
            <span>{storeTagline}</span>
          </div>
        </button>
        <form className="search-box" onSubmit={handleSearch}>
          <input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Cari TV, kulkas, AC, mesin cuci..."
          />
          <button type="submit">
            <MdSearch />
          </button>
        </form>
        <button className="cart-button" type="button" onClick={() => setShowCartDrawer(true)}>
          <MdShoppingCart />
          {cartCount > 0 && <span>{cartCount}</span>}
        </button>
        </div>
      </header>

      <nav className="category-nav">
        <div className="page-container">
          <button
            className={selectedKategori === "SEMUA" ? "active" : ""}
            type="button"
            onClick={() => {
              setSelectedKategori("SEMUA");
              navigate("/");
            }}
          >
            Semua Produk
          </button>
          {kategoris.map((kategori) => (
            <button
              key={kategori.id}
              className={String(selectedKategori) === String(kategori.id) ? "active" : ""}
              type="button"
              onClick={() => {
                setSelectedKategori(kategori.id);
                navigate("/");
              }}
            >
              {kategori.nama}
            </button>
          ))}
        </div>
      </nav>

      {toast && <div className="toast-message">{toast}</div>}

      {error && <div className="catalog-alert">{error}</div>}

      {loading ? (
        <div className="catalog-state">Memuat katalog produk...</div>
      ) : (
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                barangs={barangs}
                kategoris={kategoris}
                selectedKategori={selectedKategori}
                setSelectedKategori={setSelectedKategori}
                displayedProducts={filteredBarangs}
                flashSaleProducts={flashSaleProducts}
                flashRef={flashRef}
                rekomendasiRef={rekomendasiRef}
                scrollRow={scrollRow}
                openProduct={openProduct}
                openFlashSale={() => navigate("/flash-sale")}
                storeTagline={storeTagline}
              />
            }
          />
          <Route path="/produk/:id" element={<DetailRoute />} />
          <Route
            path="/flash-sale"
            element={
              <FlashSalePage
                flashSaleProducts={flashSaleProducts}
                backToHome={backToHome}
                openProduct={openProduct}
              />
            }
          />
          <Route
            path="/keranjang"
            element={
              <CartPage
                cartItems={cartItems}
                cartCount={cartCount}
                cartTotal={cartTotal}
                backToHome={backToHome}
                updateCartItem={updateCartItem}
                deleteCartItem={deleteCartItem}
                goToCheckout={() => navigate("/checkout")}
              />
            }
          />
          <Route
            path="/checkout"
            element={
              <CheckoutPage
                cartItems={cartItems}
                cartCount={cartCount}
                cartTotal={cartTotal}
                checkoutForm={checkoutForm}
                setCheckoutForm={setCheckoutForm}
                submitCheckout={submitCheckout}
                backToCart={() => navigate("/keranjang")}
                backToHome={backToHome}
              />
            }
          />
          <Route
            path="/checkout/berhasil"
            element={
              <ReceiptPage
                orderResult={orderResult}
                downloadOrderPdf={downloadOrderPdf}
                getWhatsappUrl={getWhatsappUrl}
                backToHome={backToHome}
              />
            }
          />
          <Route path="/tentang-kami" element={<AboutPage />} />
          <Route path="/kontak" element={<ContactPage />} />
        </Routes>
      )}
      <footer className="site-footer">
        <div className="page-container footer-grid">
          <div>
            <h3>{storeName}</h3>
            <p>{storeTagline}</p>
            <p>{storeAddress}</p>
          </div>
          <div>
            <h4>Menu</h4>
            <button type="button" onClick={backToHome}>Beranda</button>
            <button type="button" onClick={() => navigate("/tentang-kami")}>Tentang Kami</button>
            <button type="button" onClick={() => navigate("/kontak")}>Kontak</button>
          </div>
          <div>
            <h4>Kontak</h4>
            <p>WhatsApp: +{whatsappNumber}</p>
            <p>{storeEmail}</p>
          </div>
        </div>
        <div className="page-container footer-warning">{warningTransfer}</div>
      </footer>
      <a className="wa-float" href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noreferrer">
        <MdWhatsapp />
      </a>
      <CartDrawer />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <StorefrontRoutes />
    </BrowserRouter>
  );
}

export default App;
